import React, { useState, useMemo, useCallback, useContext, useRef } from "react";
import TechStafferBankerTimeLogModal from "./tech-staffer-banker-time-log-modal";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { BankerWidgetExcelExport } from "./tech-staffer-banker-time-log-helper";
import PropTypes from "prop-types";
import { LoginContext } from "../../../../../services/api/authentication-api";
import {
  createColumnMenu,
  formatLastUpdatedDate,
  renderlastUpdatedTooltipHeader,
  renderTooltipHeader,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { getLastUpdatedDateColorWeeks, NameAndNoDealsBadge } from "../../../dashboard-util/dashboard-utils";
import { FluentAvatar } from "../../../../../components/FluentUI/Fluent-Avatar";
import {
  getInitialDetailState,
  saveDetailStateToStorage,
  clearDetailStateFromStorage,
  flattenTechMembers,
  getMemberFilteredSortedData,
  useTechMemberGroupToggle,
  getAccordionTechMemberRowRender,
  TechStafferNoDealOnFilterState,
  TechStafferNoBankersEmptyState,
  TechStafferNoBankersOnSearchState,
} from "../tech-staffer-helper";
import TechStafferBankerTimeLogHeader from "./tech-staffer-banker-time-log-header";
import "../tech-staffer-time-log.scss";

const TechStafferBankerTimeLog = ({ techMembers = [], isStaffer = false, subTitle = "Technology" }) => {
  const user = useContext(LoginContext);
  const [memberState, setMemberState] = useState(() =>
    getInitialDetailState(user?.userID, "techBankerTimeLog")
  );
  const [searchValue, setSearchValue] = useState("");
  const [expandClass, setExpandClass] = useState("");
  const [selectedBanker, setSelectedBanker] = useState(null);
  const [showBankerModal, setShowBankerModal] = useState(false);
  const _exportRef = useRef(null);

  const handleBankerClick = useCallback((row) => {
    setSelectedBanker(row);
    setShowBankerModal(true);
  }, []);
  const { groupState, toggleGroup, setGroupState } = useTechMemberGroupToggle();

  const flattenedData = useMemo(() => flattenTechMembers(techMembers), [techMembers]);

  const filteredData = useMemo(() => {
    let data = getMemberFilteredSortedData(flattenedData, memberState.filter, memberState.sort);
    if (searchValue.trim()) {
      const lower = searchValue.trim().toLowerCase();
      data = data.filter((item) => item.Banker?.toLowerCase().includes(lower));
    }
    return data;
  }, [flattenedData, memberState.filter, memberState.sort, searchValue]);

  const columnMenu = createColumnMenu(flattenedData);
  const isEmpty = filteredData.length === 0;
  const isFilterEmptyState = isEmpty && (memberState.filter?.filters?.length ?? 0) > 0;
  const isSearchEmptyState = isEmpty && !isFilterEmptyState && !!searchValue.trim();
  const finalGridData = isEmpty
    ? [{ isEmptyRow: true, isFilterEmptyState, isSearchEmptyState }]
    : filteredData;

  const AccordionRowRender = useMemo(
    () => getAccordionTechMemberRowRender(filteredData, groupState, toggleGroup, 6, handleBankerClick),
    [filteredData, groupState, toggleGroup, handleBankerClick]
  );

  const columnIsFiltered = useCallback(
    (fieldName) => isFilteredColumn(fieldName, memberState.filter),
    [memberState.filter]
  );

  const resetDataState = () => {
    setSearchValue("");
    const resetState = { sort: [], filter: null };
    setMemberState(resetState);
    clearDetailStateFromStorage(user?.userID, "techBankerTimeLog");
    setGroupState({ Analyst: true, Associate: true });
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value.trimStart());
  };

  const expandWidget = () => {
    setExpandClass(expandClass === "" ? "widgetExpanded" : "");
  };

  return (
    <div
      className={`tech-staffer-time-log clickable-row ${expandClass} dashboard-container-shadow`}
    >
      <TechStafferBankerTimeLogHeader
        isStaffer={isStaffer}
        resetDataState={resetDataState}
        memberState={memberState}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        expandWidget={expandWidget}
        expandClass={expandClass}
        onExport={() => _exportRef.current?.save()}
        subTitle={subTitle}
      />
      <div
        className="detail-grid-scroll-container energy-staffer-widgets"
        data-testid="banker-time-log-grid"
      >
        <Grid
          data={finalGridData}
          dataItemKey="UserId"
          sortable={true}
          sort={memberState.sort}
          filter={memberState.filter}
          onSortChange={(e) => {
            const newState = { ...memberState, sort: e.sort };
            setMemberState(newState);
            saveDetailStateToStorage(user?.userID, newState, "", "techBankerTimeLog");
            setGroupState({ ...groupState });
          }}
          onFilterChange={(e) => {
            const newState = { ...memberState, filter: e.filter };
            setMemberState(newState);
            saveDetailStateToStorage(user?.userID, newState, "", "techBankerTimeLog");
          }}
          rowRender={AccordionRowRender}
          resizable={true}
        >
          <Column
            field="Banker"
            title={renderTooltipHeader("Banker")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("Banker") ? "filtered" : ""}
            cell={(props) => {
              if (techMembers?.length === 0) {
                return (
                  <td colSpan={6} className="k-table-td">
                    <TechStafferNoBankersEmptyState />
                  </td>
                );
              }
              if (props.dataItem.isEmptyRow) {
                return (
                  <td colSpan={6} className="k-table-td">
                    {props.dataItem.isFilterEmptyState ? (
                      <TechStafferNoDealOnFilterState resetDataState={resetDataState} />
                    ) : props.dataItem.isSearchEmptyState ? (
                      <TechStafferNoBankersOnSearchState searchValue={searchValue} resetDataState={resetDataState} />
                    ) : (
                      <TechStafferNoBankersEmptyState />
                    )}
                  </td>
                );
              }
              return (
                <td className="background-white k-table-td">
                  <div
                    className="dashboard-username"
                    style={{ pointerEvents: "none" }}
                  >
                    <FluentAvatar
                      size={28}
                      name={props.dataItem.Banker}
                      className="task-member-user mr-2"
                    />
                    <NameAndNoDealsBadge
                      name={props.dataItem.Banker}
                      activeDeals={props.dataItem.TechProjects?.length > 0}
                      text="Deals"
                    />
                  </div>
                </td>
              );
            }}
          />
          <Column
            field="LastWeekHours"
            title={renderTooltipHeader("Last Wk Hrs")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("LastWeekHours") ? "filtered" : ""}
            cell={(props) =>
              techMembers?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="summary-cell k-table-td">
                  {props.dataItem.LastWeekHours ?? "–"}
                </td>
              )
            }
          />
          <Column
            field="WeekendHours"
            title={renderTooltipHeader("Last Wknd Hrs")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
            cell={(props) =>
              techMembers?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="summary-cell k-table-td">
                  {props.dataItem.WeekendHours ?? "–"}
                </td>
              )
            }
          />
          <Column
            field="TotalHours"
            title={renderTooltipHeader("Total Hrs")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("TotalHours") ? "filtered" : ""}
            cell={(props) =>
              techMembers?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="summary-cell k-table-td">
                  {props.dataItem.TotalHours ?? "–"}
                </td>
              )
            }
          />
          <Column
            field="CurrentWeekHours"
            title={renderTooltipHeader("Current Wk Est")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("CurrentWeekHours") ? "filtered" : ""}
            cell={(props) =>
              techMembers?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td className="summary-cell k-table-td">
                  {props.dataItem.CurrentWeekHours ?? "–"}
                </td>
              )
            }
          />
          <Column
            field="LastUpdated"
            title={renderlastUpdatedTooltipHeader("Last Updated")}
            columnMenu={columnMenu}
            filter="date"
            headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
            cell={(props) =>
              techMembers?.length > 0 &&
              !props.dataItem.isEmptyRow && (
                <td
                  className="background-white k-table-td"
                  style={{
                    color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated),
                  }}
                >
                  <span style={{ pointerEvents: "none" }}>
                    {formatLastUpdatedDate(props.dataItem.LastUpdated)}
                  </span>
                </td>
              )
            }
          />
        </Grid>
      </div>
      {showBankerModal && selectedBanker && (
        <TechStafferBankerTimeLogModal
          show={showBankerModal}
          onHide={() => setShowBankerModal(false)}
          selectedBanker={selectedBanker}
        />
      )}
      <BankerWidgetExcelExport data={filteredData} ref={_exportRef} />
    </div>
  );
};

TechStafferBankerTimeLog.propTypes = {
  techMembers: PropTypes.array,
  isStaffer: PropTypes.bool,
  subTitle: PropTypes.string,
};

export default TechStafferBankerTimeLog;

export { TechStafferBankerTimeLogModal };
